import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";

/**
 * Outlet wordmarks. Each entry can later be swapped for a single-colour logo SVG:
 * add `logo: "<imported svg url>"` and the name stays as alt text.
 */
const OUTLETS: { name: string; logo?: string }[] = [
  { name: "Campaign Germany" },
  { name: "OMR" },
  { name: "Business Punk" },
  { name: "W&V" },
  { name: "Frankfurter Allgemeine" },
  { name: "Seriesly Berlin" },
  { name: "Medientage München" },
];

function Wordmark({ outlet }: { outlet: { name: string; logo?: string } }) {
  if (outlet.logo) {
    return (
      <img
        src={outlet.logo}
        alt={outlet.name}
        loading="lazy"
        className="h-5 md:h-6 w-auto opacity-45"
      />
    );
  }
  return (
    <span className="font-display text-base md:text-lg tracking-tight text-foreground/45">
      {outlet.name}
    </span>
  );
}

function Row({ ariaHidden }: { ariaHidden?: boolean }) {
  return (
    <div
      aria-hidden={ariaHidden || undefined}
      className="flex shrink-0 items-center gap-8 md:gap-12 pr-8 md:pr-12"
    >
      {OUTLETS.map((outlet) => (
        <span key={outlet.name} className="flex items-center gap-8 md:gap-12">
          <Wordmark outlet={outlet} />
          <span aria-hidden="true" className="text-foreground/25">·</span>
        </span>
      ))}
    </div>
  );
}

export function FeaturedInMarquee() {
  const { language, localizedPath } = useLanguage();
  const heading = language === "de" ? "Bekannt aus" : "Featured in";

  return (
    <section className="px-6 py-10 md:py-14" aria-label={heading}>
      <div className="max-w-[1400px] mx-auto">
        <p className="text-label mb-5">{heading}</p>
        <Link
          to={localizedPath("/presse")}
          className="block group"
          aria-label={heading}
        >
          {/* Animated marquee (hidden when the visitor prefers reduced motion) */}
          <div className="overflow-hidden motion-reduce:hidden [mask-image:linear-gradient(to_right,transparent,black_6%,black_94%,transparent)]">
            <div className="flex w-max animate-marquee-slow group-hover:[animation-play-state:paused]">
              <Row />
              <Row ariaHidden />
            </div>
          </div>
          {/* Static single-line fallback for reduced motion */}
          <div className="hidden motion-reduce:block overflow-hidden">
            <div className="flex w-max">
              <Row />
            </div>
          </div>
        </Link>
      </div>
    </section>
  );
}
